import { useCallback } from 'react';
import {
  DynamicFieldProperty_Enum,
  Template,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { useTranslation } from '@frontend/i18n';
import {
  LinkData,
  TEMPLATE_TYPE_LINK_MAP,
  useGetDefaultTemplate,
  UseTemplateFlowResult,
} from '@frontend/integrated-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { useTemplateMessagingStore } from '../stores/template-selector-store';
import { UseTemplateFlowArgs } from '../types';

type UseFormsTemplateFlowArgs = UseTemplateFlowArgs & {
  disabled?: boolean;
  personPhone: string;
  showFormsTeaser?: boolean;
};

export const useFormsTemplateFlow = ({
  disabled,
  groupId,
  personId,
  personPhone,
  renderTemplate,
  modalControl,
  showFormsTeaser,
}: UseFormsTemplateFlowArgs): UseTemplateFlowResult => {
  const { t } = useTranslation('inbox-templates');
  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: [groupId] });
  const { getLocationName } = useAppScopeStore();
  const { selectedMessageTemplate, setSelectedMessageTemplateType, setSelectedMessageTemplate } =
    useTemplateMessagingStore(
      'selectedMessageTemplate',
      'setSelectedMessageTemplateType',
      'setSelectedMessageTemplate'
    );

  const handleSelectFormTemplate = useCallback(
    (template?: Template) => {
      setSelectedMessageTemplateType?.(TemplateType_Slug.MANUAL_FORMS_MESSAGE);
      setSelectedMessageTemplate?.(template);
    },
    [setSelectedMessageTemplateType, setSelectedMessageTemplate]
  );

  const lastUsedFormTemplate = getDefaultTemplate({ type: TemplateType_Slug.MANUAL_FORMS_MESSAGE });

  return {
    popoverItem: !!personId
      ? {
          iconName: 'forms',
          label: t('Forms Template'),
          trackingId: `${InboxPrefixes.Thread}-forms-template-button`,
          onClick: !disabled
            ? () => {
                handleSelectFormTemplate(lastUsedFormTemplate);
              }
            : undefined,
        }
      : undefined,
    tabItem: {
      disabled: !personId || showFormsTeaser,
      tabLabel: t('Forms'),
      disabledLabel: showFormsTeaser ? t('Call to upgrade') : t('Contact Not Saved'),
      templateSelectorProps: {
        groupId,
        personId,
        personPhone,
        templateTypes: [TemplateType_Slug.MANUAL_FORMS_MESSAGE],
        groupIds: [groupId],
        onSelectTemplate: (template: Template) => {
          handleSelectFormTemplate(template);
        },
        onInsert: ({ appointmentId, appointmentAt, submissionId, formOrPacketLink }) => {
          const resolvedTemplate = selectedMessageTemplate ?? lastUsedFormTemplate;
          if (resolvedTemplate) {
            const formRelatedType = Object.keys(TEMPLATE_TYPE_LINK_MAP).includes(resolvedTemplate.templateTypeSlug)
              ? TEMPLATE_TYPE_LINK_MAP[resolvedTemplate.templateTypeSlug as keyof typeof TEMPLATE_TYPE_LINK_MAP]
                  ?.relatedType
              : undefined;
            const linkData: LinkData[] = [
              { link: formOrPacketLink, relatedId: submissionId, relatedType: formRelatedType },
            ];
            if (appointmentId)
              linkData.push({
                link: formOrPacketLink,
                relatedId: appointmentId,
                relatedType: RelatedType.RELATED_TYPE_APPOINTMENT,
              });
            renderTemplate({
              template: resolvedTemplate,
              linkData,
              additionalBindings: {
                [DynamicFieldProperty_Enum.FORMS_LINK]: formOrPacketLink,
                [DynamicFieldProperty_Enum.SITE_NAME]: getLocationName(groupId),
                ...(appointmentAt && { [DynamicFieldProperty_Enum.EVENT_AT]: appointmentAt }),
              },
            });
            setSelectedMessageTemplate();
            modalControl.closeModal();
          }
        },
      },
    },
  };
};
